import { Box, Divider, Stack } from '@mui/material';
import React from 'react';
import {
  StoerkIdRequestAPI,
  WorkspaceAPI,
} from '../../../redux/stoerkID/StoerkIdAPI.model';
import StoerkIdHistory from './StoerkIdHistory';
import StoerkIdRequestStage from './StoerkIdRequestStage/StoerkIdRequestStage';
import StoerkIdStatus from './StoerkIdStatus/StoerkIdStatus';
import { StoerkIdTeamMembers } from './StoerkIdTeamMembers';
import CustomerActions from '../CustomerDetailPage/CustomerRequestActions/CustomerActions';
import SelectWorkspace from './SelectWorkspace';
import StoerkIdDevices from './StoerkIdDevices';
import CustomerWorkspaceActions from '../CustomerDetailPage/CustomerWorkspaceActions/CustomerWorkspaceActions';
import { useAppSelector } from '../../../redux/store.model';
import { getFeatureToggle } from '../../../featureToggle';
import ListPolices from '../../Incident/Policy/ListPolices';
import { useUserRightUtil } from '../../../util/rights';
import SubWorkspacesOverview from './SubWorkspacesOverview/SubWorkspacesOverview';
import ParentWorkspaceInfo from './ParentWorkspaceInfo/ParentWorkspaceInfo';
import StoerkIdAPI from '../../../redux/stoerkID/StoerkIdAPI';
import { skipToken } from '@reduxjs/toolkit/query';

export interface StoerkIdDetailsProps {
  workspace?: WorkspaceAPI;
  workspaces?: WorkspaceAPI[];
  requests?: StoerkIdRequestAPI[];
  requestActive?: StoerkIdRequestAPI;
  onChangeWorkspace?: (workspace: WorkspaceAPI) => void;
  showStoerkIDInfo?: boolean;
  showAccountData?: boolean;
  showRequestInfo?: boolean;
  showBillingInfo?: boolean;
  /** even if all users could see theris policies,
   * StoerkSales should not */
  showPolicies?: boolean;
  showRequestWorkspaceActions?: boolean;
  showWorkspaceActions?: boolean;
  isStoerkIdPage?: boolean;
}

function StoerkIdDetails(props: StoerkIdDetailsProps) {
  const {
    workspace,
    requests,
    onChangeWorkspace,
    workspaces = [],
    requestActive,
    showAccountData,
    showBillingInfo,
    showRequestInfo,
    showPolicies,
    showRequestWorkspaceActions,
    showWorkspaceActions,
  } = props;

  const workspaceId = workspace?.id ?? '';
  const featureToggle = useAppSelector(getFeatureToggle);
  const userRightUtil = useUserRightUtil();
  const showWorkspaceInfo = userRightUtil.canViewWorkspace(workspaceId);
  const editWorkspaceInfo = userRightUtil.canUpdateWorkspace(workspaceId);
  const showWorkspaceRequest =
    userRightUtil.canViewWorkspaceRequest(workspaceId);
  const editWorkspaceEditRequest =
    userRightUtil.canUpdateWorkspaceRequest(workspaceId);
  const showUsers = userRightUtil.canViewWorkspaceUsers(workspaceId);
  const showDevices = userRightUtil.canViewWorkspaceDevices(workspaceId);
  const isInRequestProcess = !workspace && !!requestActive;

  // Check if workspace has subworkspaces or a parent
  const { data: subWorkspaces } = StoerkIdAPI.useGetSubWorkspacesQuery(
    workspace?.id ?? skipToken
  );
  const hasSubWorkspaces =
    !!subWorkspaces && Object.keys(subWorkspaces).length > 0;

  const { data: parentWorkspaceInfo } = StoerkIdAPI.useGetWorkspaceQuery(
    workspace?.parentId ?? skipToken
  );
  const hasParent = !!workspace?.parentId && !!parentWorkspaceInfo;

  const sections = [
    showWorkspaceInfo && workspaces?.length > 2 ? (
      <SelectWorkspace
        key="select"
        workspaces={workspaces}
        workspace={workspace}
        onChangeWorkspace={onChangeWorkspace}
      />
    ) : null,
    showWorkspaceInfo || isInRequestProcess ? (
      <StoerkIdStatus
        key="status"
        workspace={workspace}
        showAccountData={showAccountData}
      />
    ) : null,
    workspace && showWorkspaceActions ? (
      <CustomerWorkspaceActions key="wsActions" workspace={workspace} />
    ) : null,
    workspace && hasSubWorkspaces ? (
      <SubWorkspacesOverview key="subWs" workspace={workspace} />
    ) : null,
    workspace && hasParent ? (
      <ParentWorkspaceInfo key="parent" workspace={workspace} />
    ) : null,
    (showWorkspaceRequest || isInRequestProcess) && requestActive ? (
      <StoerkIdRequestStage
        key="requestStage"
        request={requestActive}
        showRequestInfo={showRequestInfo}
        showBillingInfo={showBillingInfo}
        isLastRequest
      />
    ) : null,
    requestActive && showRequestWorkspaceActions && editWorkspaceEditRequest ? (
      <CustomerActions key="custActions" request={requestActive} />
    ) : null,
    workspace && showUsers ? (
      <StoerkIdTeamMembers key="team" workspace={workspace} />
    ) : null,
    workspace && showDevices ? (
      <StoerkIdDevices key="devices" workspaceId={workspace.id} />
    ) : null,
    workspace && showPolicies && featureToggle.alarmManager ? (
      <ListPolices key="policies" workspaceId={workspace.id} />
    ) : null,
    requests?.length && (showWorkspaceRequest || isInRequestProcess) ? (
      <StoerkIdHistory key="history" requests={requests} />
    ) : null,
  ].filter(Boolean);

  return (
    <Box marginY={2}>
      <Stack spacing={2} divider={<Divider />}>
        {sections}
      </Stack>
    </Box>
  );
}

export default StoerkIdDetails;
